import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, LinearProgress, Stack, styled, Icon, IconButton, TabPanel, Tabs, Tooltip, Typography, Fade, Popper } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { PureList } from "@bundles/parts/components/PureList"
import { ECrossSellingType } from "@tm/models"
import { ParticularStateProvider } from "@bundles/parts/components/PureList/ContextProviders/Particular/ParticularContext"
import { getUIA, usePrevious } from "@tm/utils"
import { useCrossSelling } from "../hooks"
import { useListOptionsContext } from "../../../ContextProvider"
import { ProductGroupButton } from "./ProductGroupButton"
import { ArticlesChain } from "./ArticlesChain"
import { ArticleListFeaturesDisableProvider } from "../../../ArticleListConfiguration/ArticleListFeaturesDisable"
import { crossSellingListId } from "../crossSellingStates"
import { CrossSellingComponentProps, CrossSellingTabType } from "../models"
import { getOrderFromElement, getUIASelector } from "../helpers"
import { useArticleListConfiguration } from "../../../ArticleListConfiguration"
import { TinyTab } from "./TinyTab"

const Container = styled(Box)({
    backgroundColor: "#fafafa",
    boxShadow: "0 1px 5px rgb(0 0 0 / 20%)",
    padding: "10px 5px 5px 20px",
    marginLeft: 16,
})

export const CrossSellingComponent = memo(
    ({
        listType,
        isAlternativeSearch,
        newSearchLoaded,
        crossSellingModalType,
        sortChanged,
        articlesCount,
        frontEndFilterChanged,
    }: CrossSellingComponentProps) => {
        const { translateText } = useLocalization()
        const {
            isLoading,
            isEnabled,
            data,
            chains,
            displayCustomerAlsoBoughtTab,
            displayCustomerAlsoBoughtButton,
            pureListContext,
            onProductGroupClick,
            onClose,
            onReset,
            preventReset,
            setPreventReset,
            referenceArticle,
        } = useCrossSelling(listType, isAlternativeSearch, crossSellingModalType)

        const maximumArticleCount = useArticleListConfiguration((x) => x.sorting.maximumArticleCount)
        const defaultMode = useArticleListConfiguration((x) => x.sorting.defaultMode)
        const tileView = useListOptionsContext((x) => x.tileView) // TODO : check why on tile/list change the tileList value toggles twice
        const compactView = useListOptionsContext((x) => x.compactView)
        const showArticleImages = useListOptionsContext((x) => x.showArticleImages)
        const prevTileView = usePrevious(tileView)
        const prevNewSearchLoaded = usePrevious(newSearchLoaded)

        const [activeNavIdentifier, setActiveNavIdentifier] = useState<CrossSellingTabType>(ECrossSellingType.alsoBoughtArticles)
        const [targetElement, setTargetElement] = useState<HTMLElement | null>(null)
        const [articleOrder, setArticleOrder] = useState<number | undefined>(undefined)

        const retryTimer = useRef<NodeJS.Timeout | null>(null)

        const displayArticlesChain = useMemo(
            () => (chains.alsoBoughtArticles?.length > 0 ? chains.alsoBoughtArticles : chains.alsoBoughtProductGroups),
            [chains.alsoBoughtArticles, chains.alsoBoughtProductGroups]
        )

        const listSortHasChanged =
            (!!sortChanged && !!referenceArticle?.productGroup?.id && sortChanged[referenceArticle.productGroup.id]) || undefined

        useEffect(() => {
            const loadBecameTrue = prevNewSearchLoaded === false && newSearchLoaded
            if (!loadBecameTrue) {
                return
            }

            if (newSearchLoaded) {
                if (preventReset && (listSortHasChanged === defaultMode || (articlesCount && maximumArticleCount <= articlesCount))) {
                    setPreventReset(false)
                } else {
                    onReset?.()
                }
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [newSearchLoaded])

        useEffect(() => {
            setActiveNavIdentifier(displayCustomerAlsoBoughtTab ? ECrossSellingType.alsoBoughtArticles : ECrossSellingType.recommendedProductGroups)
        }, [displayCustomerAlsoBoughtTab])

        useEffect(() => {
            setTargetElement(null)
        }, [tileView])

        function resolveTargetElementAndScroll(retries = 4) {
            if (!referenceArticle?.internalId || !newSearchLoaded || !isEnabled) {
                setTargetElement(null)
                return
            }

            const elementData = getUIA(tileView ? "ArticleBasketButton" : "Article", referenceArticle.internalId)
            const selector = getUIASelector(elementData)

            if (!selector) {
                setTargetElement(null)
                return
            }

            requestAnimationFrame(() => {
                const element = document.querySelector<HTMLElement>(selector)

                if (retryTimer.current) {
                    clearTimeout(retryTimer.current)
                }

                if (element) {
                    setTargetElement(element)
                    const order = getOrderFromElement(element)
                    setArticleOrder(order)

                    if (
                        (listSortHasChanged === defaultMode || (articlesCount && maximumArticleCount <= articlesCount)) &&
                        isAlternativeSearch &&
                        prevNewSearchLoaded === false &&
                        newSearchLoaded &&
                        preventReset
                    ) {
                        element.scrollIntoView({ behavior: "smooth", block: "center" })
                    }
                } else if (retries > 0) {
                    retryTimer.current = setTimeout(() => resolveTargetElementAndScroll(retries - 1), 550)
                } else {
                    setTargetElement(null)
                }
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(resolveTargetElementAndScroll, [referenceArticle, tileView, newSearchLoaded, listSortHasChanged, frontEndFilterChanged])

        const handleNavSelect = useCallback((_: unknown, nextNavIdentifier: CrossSellingTabType) => {
            requestAnimationFrame(() => {
                setActiveNavIdentifier(nextNavIdentifier)
            })
        }, [])

        if (!isEnabled || !newSearchLoaded) {
            return null
        }

        const renderTabButtons = () => (
            <Stack direction="row" justifyContent="space-between" sx={{ marginTop: "-4px" }}>
                <Tabs value={activeNavIdentifier} onChange={handleNavSelect} sx={{ "& .MuiTabs-indicator": { borderRadius: "3px" } }}>
                    {displayCustomerAlsoBoughtTab && <TinyTab value={ECrossSellingType.alsoBoughtArticles} label={translateText(13838)} />}
                    {!!data.recommendedProductGroups?.length && (
                        <TinyTab value={ECrossSellingType.recommendedProductGroups} label={translateText(14014)} />
                    )}
                </Tabs>
                <Tooltip title={translateText(70)}>
                    <IconButton onClick={onClose} sx={{ padding: "0 0.5em" }}>
                        <Icon name="close" />
                    </IconButton>
                </Tooltip>
            </Stack>
        )

        const renderCustomerAlsoBought = () => (
            <Fade in={activeNavIdentifier === ECrossSellingType.alsoBoughtArticles} timeout={100}>
                <Box>
                    <ArticlesChain articleChain={displayArticlesChain} showArticleImages={showArticleImages} compactView={compactView} />
                    <Stack>
                        <Typography ml={0.5} mb={1} variant="body2">
                            {translateText(13837)}
                        </Typography>
                        {displayCustomerAlsoBoughtButton && !!data.alsoBoughtProductGroups?.length && (
                            <Box>
                                {data.alsoBoughtProductGroups.map((group) => (
                                    <ProductGroupButton
                                        key={group?.name}
                                        productGroup={group?.name}
                                        onProductGroupClick={() => onProductGroupClick?.(group, ECrossSellingType.alsoBoughtProductGroups)}
                                    />
                                ))}
                            </Box>
                        )}
                        {!displayCustomerAlsoBoughtButton && data.alsoBoughtArticles?.length && (
                            <ArticleListFeaturesDisableProvider disableSelection>
                                <ParticularStateProvider value={pureListContext}>
                                    <PureList listId={crossSellingListId} listType={crossSellingListId} />
                                </ParticularStateProvider>
                            </ArticleListFeaturesDisableProvider>
                        )}
                        <Icon
                            name="poweredByTM"
                            sx={{ width: "180px", height: "20px", alignSelf: "end", opacity: "0.5", marginTop: "-6px" }}
                            viewBox="0 0 180 20"
                        />
                    </Stack>
                </Box>
            </Fade>
        )

        const renderRecommendedProductGroups = () => (
            <Fade in={activeNavIdentifier === ECrossSellingType.recommendedProductGroups} timeout={100}>
                <Box>
                    <ArticlesChain articleChain={chains.recommendedProductGroups} showArticleImages={showArticleImages} compactView={compactView} />
                    <Stack>
                        <Typography ml={0.5} mb={1} variant="body2">
                            {translateText(14015)}
                        </Typography>
                        {!!data.recommendedProductGroups?.length && (
                            <Box>
                                {data.recommendedProductGroups.map((group) => (
                                    <ProductGroupButton
                                        key={group?.name}
                                        productGroup={group?.name}
                                        onProductGroupClick={() => onProductGroupClick?.(group, ECrossSellingType.recommendedProductGroups)}
                                    />
                                ))}
                            </Box>
                        )}
                        <Icon
                            name="poweredByTM"
                            sx={{ width: "180px", height: "20px", alignSelf: "end", opacity: "0.5", marginTop: "-6px" }}
                            viewBox="0 0 180 20"
                        />
                    </Stack>
                </Box>
            </Fade>
        )

        const getDefaultCrossSelling = () => {
            if (articleOrder === undefined) {
                return null
            }

            return (
                <Container sx={{ order: articleOrder + 1 }}>
                    {isLoading && <LinearProgress />}
                    {renderTabButtons()}
                    <TabPanel value={ECrossSellingType.alsoBoughtArticles} index={activeNavIdentifier}>
                        {renderCustomerAlsoBought()}
                    </TabPanel>
                    <TabPanel value={ECrossSellingType.recommendedProductGroups} index={activeNavIdentifier}>
                        {renderRecommendedProductGroups()}
                    </TabPanel>
                </Container>
            )
        }

        const getCrossSellingView = () => {
            if (!targetElement) {
                return null
            }

            if (tileView && prevTileView === tileView) {
                return (
                    <Popper
                        open
                        anchorEl={targetElement}
                        placement="bottom-start"
                        modifiers={[
                            { name: "offset", options: { offset: [0, 8] } },
                            { name: "flip", options: { fallbackPlacements: ["top-start"] } },
                        ]}
                        sx={{ zIndex: 25 }}
                    >
                        <Box ml="-1em" maxWidth="75em" minWidth="65em">
                            {getDefaultCrossSelling()}
                        </Box>
                    </Popper>
                )
            }

            return getDefaultCrossSelling()
        }

        return getCrossSellingView()
    }
)
